import Typograf from "typograf";

// Map app fontOption → Typograf locale. Russian templates may include
// English words (product names, URLs), so both locales are enabled. Pure
// English templates should NOT run Russian rules — otherwise Russian
// abbreviation/NBSP heuristics fire on English content.
const LOCALE_MAP = {
  ru: ["ru", "en-US"],
  en: ["en-US"],
};

const DEFAULT_LOCALE = ["en-US"];

// Keep NBSP-insertion rules enabled — binding articles/prepositions to the
// next word is the whole point of typograf. Only disable trim rules, which
// would strip the zero-width marker used for empty fields (editableText.js).
const DISABLED_RULES = [
  "common/space/trimLeft",
  "common/space/trimRight",
];

const cache = new Map();

function getInstance(locales) {
  const key = locales.join(",");
  let tp = cache.get(key);
  if (!tp) {
    tp = new Typograf({
      locale: locales,
      disableRule: DISABLED_RULES,
      htmlEntity: {type: "default"},
    });
    cache.set(key, tp);
  }
  return tp;
}

function resolveLocale(fontOption) {
  return LOCALE_MAP[fontOption] || DEFAULT_LOCALE;
}

// Replace spaces in product names with non-breaking spaces to prevent wrapping.
// This function is applied regardless of typograf setting.
export function ensureProductNameNbsp(text) {
  if (typeof text !== "string" || text.length === 0) return text;
  let out = text;
  out = out.replace(/IntelliJ\s+IDEA/g, "IntelliJ\u00A0IDEA");
  return out;
}

export function applyTypograf(text, fontOption) {
  if (typeof text !== "string" || text.length === 0) return text;
  try {
    let result = getInstance(resolveLocale(fontOption)).execute(text);
    result = ensureProductNameNbsp(result);
    return result;
  } catch (err) {
    console.warn("[typograf] failed to process text", err);
    return text;
  }
}

// Best-effort reverse of the visible substitutions typograf introduces:
// NBSP variants, typographic quotes, em/en dashes, ellipsis. Used when the
// user flips the Typograf toggle off. Note: this is lossy — characters the
// user typed intentionally (e.g. a deliberate em dash) will also be flattened.
// `×` is deliberately NOT reverted — it's a legitimate math symbol.
// Unicode escapes kept explicit so the NBSP variants are visible in diffs
// and don't trigger eslint's no-irregular-whitespace. `&nbsp;` entity is
// matched alongside U+00A0 because ContentEditable reads HTML via
// `.innerHTML`, which serializes U+00A0 back to the entity form.
const REVERT_REPLACEMENTS = [
  [/[\u00A0\u202F\u2009]|&nbsp;/g, " "],
  [/…/g, "..."],
  [/[—–]/g, "-"],
  [/[«»“”„‟]/g, '"'],
  [/[‘’‚‛]/g, "'"],
];

// Disabling Typograf reverts its substitutions — but "IntelliJ IDEA" is a hard
// exception: the product name must NEVER wrap, regardless of the Typograf
// toggle. So after the generic revert flattens every NBSP back to a space, we
// re-bind the product name with a non-breaking space as the final step,
// overriding the NBSP revert above.
export function revertTypograf(text) {
  if (typeof text !== "string" || text.length === 0) return text;
  let out = text;
  for (const [pattern, replacement] of REVERT_REPLACEMENTS) {
    out = out.replace(pattern, replacement);
  }
  out = ensureProductNameNbsp(out);
  return out;
}
